<template>
  <div class="relative" :class="dropdownClasses">
    <label :class="dropdownSelectedOption" :for="id">{{ labelName }}</label>
    <select
      :id="id"
      v-model="selectedOption"
      class="dropdown-input"
      :aria-invalid="hasError"
      :aria-describedby="ariaDescribedByName"
      :required="required"
      :disabled="disabled"
      :name="name"
      @blur="$emit('blur')"
    >
      <!-- needs to accept an object or an array -->
      <option
        v-for="(option, index) in options"
        :id="index"
        :key="index"
        :value="option.hasOwnProperty('value') ? option.value : option"
      >
        {{ option.display ? option.display : option }}
      </option>
    </select>
    <slot />
    <HelperText :id="id" :status="status" :text="helperText" />
  </div>
</template>

<script lang="ts">
import helperStatus from '../../helpers/status';
import selectionOptions from '../../helpers/selection';
import HelperText from '../helper_text/HelperText.vue';

export default {
  name: 'Dropdown',
  components: {
    HelperText,
  },
  props: {
    labelName: {
      type: String,
      required: true,
      validator(value: String) {
        return !!value;
      },
    },
    id: {
      type: String,
      required: true,
      validator(value: String) {
        return !!value;
      },
    },
    options: {
      type: Array as () => selectionOptions[],
      required: true,
    },
    name: {
      type: String,
      default: '',
    },
    value: {
      type: [String, Number],
      default: '',
    },
    status: {
      type: Number as () => helperStatus,
      default: helperStatus.Neutral,
    },
    helperText: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
  },
  data(): any {
    return {
      selectedOption: this.value,
    };
  },
  computed: {
    ariaDescribedByName(): String | null {
      if (!this.helperText) {
        return null;
      }
      return `${this.id}-helper`;
    },
    dropdownSelectedOption(): String {
      if (this.selectedOption) {
        return 'dropdown-label-filled';
      }
      return 'dropdown-label';
    },
    dropdownClasses(): String {
      if (this.hasError) {
        return 'dropdown-error';
      }
      return 'dropdown';
    },
    hasError(): Boolean {
      return this.status === helperStatus.Error;
    },
  },
  watch: {
    // watch for data changes so the parent can be notified
    selectedOption(value: String | Number) {
      if (value) {
        this.$emit('input', value);
      }
    },
    value(v: String | Number) {
      // watching both the prop and the data causes infinite feedback loops,
      // checking the value has actually changed prevents the loop.
      if (v !== this.selectedOption) {
        this.selectedOption = v;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.dropdown-input {
  appearance: none;
  background-image:
    linear-gradient(45deg, transparent 50%, #05070f 50%), linear-gradient(135deg, #05070f 50%, transparent 50%),
    linear-gradient(to right, transparent, transparent);
  background-position:
    calc(100% - 20px) calc(45% + 3px),
    calc(100% - 15px) calc(45% + 3px),
    calc(100% - 14px);
  background-size:
    8px 5px,
    5px 5px,
    1px 1.5rem;
  background-repeat: no-repeat;
  background-color: theme('colors.foreground');
  border: 1px solid theme('colors.grey.graphics.subdued');
  border-radius: theme('borderRadius.xl');
  color: theme('colors.grey.body');
  display: block;
  font-family: theme('fontFamily.sans');
  font-size: theme('fontSize.m');
  font-weight: theme('fontWeight.regular');
  height: 56px;
  padding: 1.5rem 3rem 0.5rem 1rem;
  letter-spacing: 0.09px;
  line-height: 1.5;
  position: relative;
  width: 100%;
  z-index: 0;
  cursor: pointer;
  &:focus {
    border: none;
    box-shadow: 0 0 0 2px theme('colors.grey.clickable');
    outline: none;
  }
  &:disabled {
    background-color: theme('colors.grey.background.subdued');
    background-image: none;
    border: none;
    color: theme('colors.grey.text.subdued');
    cursor: not-allowed;
  }
}
.dropdown-error .dropdown-input {
  border: none;
  box-shadow: 0 0 0 2px theme('colors.critical.DEFAULT');
  border-radius: theme('borderRadius.xl');
}
.dropdown-label {
  color: theme('colors.grey.text.subdued');
  font-family: theme('fontFamily.sans');
  font-size: theme('fontSize.m');
  font-weight: theme('fontWeight.regular');
  letter-spacing: 0.09px;
  position: absolute;
  left: 1rem;
  top: 1.125rem;
  transition:
    top 250ms cubic-bezier(0.4, 0, 0.2, 1),
    font-size 250ms cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1;
  pointer-events: none;
}
select:focus + .dropdown-label {
  font-size: theme('fontSize.xs');
  top: 6px;
  transition:
    top 250ms cubic-bezier(0.4, 0, 0.2, 1),
    font-size 250ms cubic-bezier(0.4, 0, 0.2, 1);
}
.dropdown-label-filled {
  color: theme('colors.grey.text.subdued');
  font-family: theme('fontFamily.sans');
  font-size: theme('fontSize.xs');
  font-weight: theme('fontWeight.regular');
  letter-spacing: 0.09px;
  position: absolute;
  left: 1rem;
  top: 6px;
  transition:
    top 150ms cubic-bezier(0.4, 0, 0.2, 1),
    font-size 150ms cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1;
}
.dropdown-icon-overridden {
  & .dropdown-input {
    background-image: none;
  }
  & .dropdown-icon {
    width: 24px;
    height: 24px;
    position: absolute;
    left: calc(100% - 2rem);
    top: 18px;
    pointer-events: none;
  }
}
</style>
